import { z } from 'zod';
import { isCalendarDate, normalizeName } from './people.js';

/**
 * Competition seasons and rule profile versions (WP07, ADR 0012). A competition season ties a
 * department's season to one competition (federation, category, division and phase structure);
 * its rule profile versions are the declarative eligibility rules, and only an activated version
 * can approve anyone. Draft, superseded and missing profiles always lead to review.
 */

export const competitionPhases = ['regular', 'playoff', 'tournament'] as const;
export type CompetitionPhase = (typeof competitionPhases)[number];

export const ruleProfileStatuses = ['draft', 'active', 'superseded'] as const;
export type RuleProfileStatus = (typeof ruleProfileStatuses)[number];

/** Why a draft cannot be activated yet; the codes are translated as `competitions.blocker.*`. */
export const ruleProfileBlockers = [
  'no_sources',
  'source_hash_missing',
  'sources_not_checked',
  'category_mismatch',
] as const;
export type RuleProfileBlocker = (typeof ruleProfileBlockers)[number];

const textSchema = (maximumLength: number) =>
  z.string().transform(normalizeName).pipe(z.string().min(1).max(maximumLength));

const categoryCodeSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_]{0,31}$/);
const calendarDateSchema = z.string().refine(isCalendarDate);

export const createCompetitionSeasonRequestSchema = z.strictObject({
  seasonId: z.uuid(),
  name: textSchema(200),
  federation: textSchema(50),
  category: categoryCodeSchema,
  division: z
    .string()
    .transform(normalizeName)
    .pipe(z.string().max(100))
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .default(null),
  format: z
    .string()
    .transform(normalizeName)
    .pipe(z.string().max(100))
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .default(null),
  phases: z
    .array(z.enum(competitionPhases))
    .min(1)
    .max(competitionPhases.length)
    .refine((phases) => new Set(phases).size === phases.length)
    .default(['regular']),
  /** IANA time zone of the competition's match days; the club's own zone when omitted. */
  timeZone: z.string().min(1).max(64).optional(),
});
export type CreateCompetitionSeasonRequest = z.infer<typeof createCompetitionSeasonRequestSchema>;

export interface CompetitionSeasonView {
  id: string;
  departmentId: string;
  seasonId: string;
  seasonLabel: string;
  name: string;
  federation: string;
  category: string;
  division: string | null;
  format: string | null;
  phases: CompetitionPhase[];
  timeZone: string;
  /** The activated rule profile version, or null while the competition has none (eligibility is then review). */
  activeRuleProfile: { id: string; versionNumber: number; activatedAt: string } | null;
  canManage: boolean;
}

/** A draft shipped with the application that a rules officer can copy into a competition season. */
export interface RuleProfileCatalogEntryView {
  key: string;
  category: string;
  division: string | null;
  validFrom: string;
  validTo: string;
  sourceCount: number;
  conditionCount: number;
  mandatoryManualCheckCount: number;
}

export const createRuleProfileDraftRequestSchema = z.strictObject({
  competitionSeasonId: z.uuid(),
  catalogKey: z.string().min(1).max(80),
});
export type CreateRuleProfileDraftRequest = z.infer<typeof createRuleProfileDraftRequestSchema>;

const sourceHashSchema = z.string().regex(/^[0-9a-f]{64}$/);

/** The officer's check of the exact source documents: a SHA-256 per source, the day checked and a note. */
export const recordSourceVerificationRequestSchema = z.strictObject({
  sourceHashes: z
    .array(z.strictObject({ sourceId: z.string().min(1).max(64), sourceHash: sourceHashSchema }))
    .max(50)
    .refine((entries) => new Set(entries.map((entry) => entry.sourceId)).size === entries.length),
  checkedOn: calendarDateSchema,
  note: textSchema(500),
});
export type RecordSourceVerificationRequest = z.infer<typeof recordSourceVerificationRequestSchema>;

export const activateRuleProfileRequestSchema = z.strictObject({
  note: textSchema(500),
});
export type ActivateRuleProfileRequest = z.infer<typeof activateRuleProfileRequestSchema>;

export interface RuleProfileSourceView {
  id: string;
  sourceHash: string | null;
}

export interface RuleProfileSummaryView {
  id: string;
  competitionSeasonId: string;
  versionNumber: number;
  category: string;
  division: string | null;
  status: RuleProfileStatus;
  validFrom: string;
  validTo: string;
  sourceCount: number;
  sourceHashCount: number;
  conditionCount: number;
  mandatoryManualCheckCount: number;
  sourcesCheckedOn: string | null;
  activatedAt: string | null;
  supersededAt: string | null;
  version: number;
  createdAt: string;
}

export interface RuleProfileDetailView extends RuleProfileSummaryView {
  sources: RuleProfileSourceView[];
  mandatoryManualChecks: { key: string; sourceClause: string }[];
  verificationNote: string | null;
  /** Empty when the draft can be activated; always empty for a profile that is not a draft. */
  blockers: RuleProfileBlocker[];
  /** The stored declarative document, for review; it is data, never executed. */
  document: Record<string, unknown>;
}

/**
 * Competition registrations (WP07, ADR 0013). A registration links one athlete to one competition
 * season through a team of the same department and season. It is independent of club membership and
 * of team and group placement. The registration mode decides which date-of-birth route and quota
 * treatment the rule profile applies.
 */

export const competitionRegistrationModes = [
  'native',
  'temporary_play_up',
  'exclusive_higher',
] as const;
export type CompetitionRegistrationMode = (typeof competitionRegistrationModes)[number];

/**
 * The competition classification an athlete plays under in one competition (ADR 0018). Rule profiles
 * use it for the girls' date-of-birth route and the national-team female approval; anyone else is
 * `general`. It is restricted data, recorded only on a registration and shown only to registrars.
 */
export const competitionClassifications = ['general', 'girls', 'national_team_female'] as const;
export type CompetitionClassification = (typeof competitionClassifications)[number];

export const competitionRegistrationStatuses = ['active', 'ended'] as const;
export type CompetitionRegistrationStatus = (typeof competitionRegistrationStatuses)[number];

const licenseReferenceSchema = z
  .string()
  .transform(normalizeName)
  .pipe(z.string().max(100))
  .transform((value) => (value === '' ? null : value))
  .nullable();

export const createCompetitionRegistrationRequestSchema = z
  .strictObject({
    competitionSeasonId: z.uuid(),
    athleteId: z.uuid(),
    teamId: z.uuid(),
    mode: z.enum(competitionRegistrationModes),
    validFrom: calendarDateSchema,
    validTo: calendarDateSchema.nullable().default(null),
    licenseReference: licenseReferenceSchema.default(null),
    classification: z.enum(competitionClassifications).default('general'),
  })
  .refine((request) => request.validTo === null || request.validTo >= request.validFrom, {
    path: ['validTo'],
  });
export type CreateCompetitionRegistrationRequest = z.infer<
  typeof createCompetitionRegistrationRequestSchema
>;

/** The licence reference, the end day and the classification of an active registration can be corrected. */
export const updateCompetitionRegistrationRequestSchema = z
  .strictObject({
    licenseReference: licenseReferenceSchema.optional(),
    validTo: calendarDateSchema.nullable().optional(),
    classification: z.enum(competitionClassifications).optional(),
  })
  .refine(
    (request) =>
      request.licenseReference !== undefined ||
      request.validTo !== undefined ||
      request.classification !== undefined,
  );
export type UpdateCompetitionRegistrationRequest = z.infer<
  typeof updateCompetitionRegistrationRequestSchema
>;

/** Ending is final: the registration keeps its history and a new one is created to register again. */
export const endCompetitionRegistrationRequestSchema = z.strictObject({
  endedOn: calendarDateSchema,
  reason: textSchema(500),
});
export type EndCompetitionRegistrationRequest = z.infer<
  typeof endCompetitionRegistrationRequestSchema
>;

export interface CompetitionRegistrationView {
  id: string;
  competitionSeasonId: string;
  athlete: { id: string; givenName: string; familyName: string };
  team: { id: string; name: string };
  mode: CompetitionRegistrationMode;
  status: CompetitionRegistrationStatus;
  validFrom: string;
  validTo: string | null;
  licenseReference: string | null;
  classification: CompetitionClassification;
  endedReason: string | null;
  version: number;
  /** Holds `competitions.manage` in the competition's department. */
  canManage: boolean;
}

/**
 * Documentary evidence (WP07, ADR 0014). A record states that a document of one type exists for an
 * athlete, who issued it and the days it is valid; it holds no copy and no health content. A new
 * record is pending; a registrar verifies or rejects it, and only verified records count towards
 * eligibility. A verified record can only be withdrawn; a rejected one never changes.
 */

/** The types the registrar can record in this release; profiles may name others, which stay review-required. */
export const documentEvidenceTypes = [
  'medical_clearance',
  'under_18_agreement',
  'promotion_agreement',
  'loan_agreement',
  'transfer_approval',
] as const;
export type DocumentEvidenceType = (typeof documentEvidenceTypes)[number];

export const evidenceVerificationStatuses = ['pending', 'verified', 'rejected'] as const;
export type EvidenceVerificationStatus = (typeof evidenceVerificationStatuses)[number];

const optionalTextSchema = (maximumLength: number) =>
  z
    .string()
    .transform(normalizeName)
    .pipe(z.string().max(maximumLength))
    .transform((value) => (value === '' ? null : value))
    .nullable();

export const createDocumentEvidenceRequestSchema = z
  .strictObject({
    athleteId: z.uuid(),
    documentType: z.enum(documentEvidenceTypes),
    issuer: optionalTextSchema(200).default(null),
    reference: optionalTextSchema(100).default(null),
    validFrom: calendarDateSchema,
    validTo: calendarDateSchema.nullable().default(null),
  })
  .refine((request) => request.validTo === null || request.validTo >= request.validFrom, {
    path: ['validTo'],
  });
export type CreateDocumentEvidenceRequest = z.infer<typeof createDocumentEvidenceRequestSchema>;

/** Only a pending record can be corrected. */
export const updateDocumentEvidenceRequestSchema = z
  .strictObject({
    issuer: optionalTextSchema(200).optional(),
    reference: optionalTextSchema(100).optional(),
    validFrom: calendarDateSchema.optional(),
    validTo: calendarDateSchema.nullable().optional(),
  })
  .refine(
    (request) =>
      request.issuer !== undefined ||
      request.reference !== undefined ||
      request.validFrom !== undefined ||
      request.validTo !== undefined,
  );
export type UpdateDocumentEvidenceRequest = z.infer<typeof updateDocumentEvidenceRequestSchema>;

export const verifyDocumentEvidenceRequestSchema = z.strictObject({
  note: optionalTextSchema(500).default(null),
});
export type VerifyDocumentEvidenceRequest = z.infer<typeof verifyDocumentEvidenceRequestSchema>;

/** A rejection (or the withdrawal of a verified record) always carries its reason. */
export const rejectDocumentEvidenceRequestSchema = z.strictObject({ reason: textSchema(500) });
export type RejectDocumentEvidenceRequest = z.infer<typeof rejectDocumentEvidenceRequestSchema>;

export interface DocumentEvidenceView {
  id: string;
  athlete: { id: string; givenName: string; familyName: string };
  /** One of {@link documentEvidenceTypes}; any other stored type is shown by its raw code. */
  documentType: string;
  issuer: string | null;
  reference: string | null;
  validFrom: string;
  validTo: string | null;
  verificationStatus: EvidenceVerificationStatus;
  decisionNote: string | null;
  version: number;
  /** Holds `competitions.manage`, so may correct, verify, reject or withdraw this record. */
  canManage: boolean;
}

/**
 * Suspensions (WP07, ADR 0015). A suspension bars an athlete from playing, until a last day or for a
 * number of games, in one competition or in every competition of the club. It starts active; a
 * registrar corrects it while it is active, marks it served, or revokes it with a reason. A served or
 * revoked suspension never changes. Only an active suspension counts towards eligibility.
 */
export const suspensionStatuses = ['active', 'served', 'revoked'] as const;
export type SuspensionStatus = (typeof suspensionStatuses)[number];

const remainingGamesSchema = z.number().int().min(0).max(99);

/** Exactly one measure: a last day (`endsOn`) or a number of games (`remainingGames`). */
export const createSuspensionRequestSchema = z
  .strictObject({
    athleteId: z.uuid(),
    /** Omitted or null: the suspension applies in every competition of the club. */
    competitionSeasonId: z.uuid().nullable().default(null),
    startsOn: calendarDateSchema,
    endsOn: calendarDateSchema.nullable().default(null),
    remainingGames: remainingGamesSchema.nullable().default(null),
    decisionReference: optionalTextSchema(100).default(null),
  })
  .refine((request) => (request.endsOn === null) !== (request.remainingGames === null), {
    path: ['endsOn'],
  })
  .refine((request) => request.endsOn === null || request.endsOn >= request.startsOn, {
    path: ['endsOn'],
  });
export type CreateSuspensionRequest = z.infer<typeof createSuspensionRequestSchema>;

/** Only an active suspension can be corrected, and its kind of measure stays the same. */
export const updateSuspensionRequestSchema = z
  .strictObject({
    startsOn: calendarDateSchema.optional(),
    endsOn: calendarDateSchema.optional(),
    remainingGames: remainingGamesSchema.optional(),
    decisionReference: optionalTextSchema(100).optional(),
  })
  .refine(
    (request) =>
      request.startsOn !== undefined ||
      request.endsOn !== undefined ||
      request.remainingGames !== undefined ||
      request.decisionReference !== undefined,
  )
  .refine((request) => request.endsOn === undefined || request.remainingGames === undefined, {
    path: ['endsOn'],
  });
export type UpdateSuspensionRequest = z.infer<typeof updateSuspensionRequestSchema>;

export const serveSuspensionRequestSchema = z.strictObject({
  note: optionalTextSchema(500).default(null),
});
export type ServeSuspensionRequest = z.infer<typeof serveSuspensionRequestSchema>;

/** A revocation always carries its reason. */
export const revokeSuspensionRequestSchema = z.strictObject({ reason: textSchema(500) });
export type RevokeSuspensionRequest = z.infer<typeof revokeSuspensionRequestSchema>;

export interface SuspensionView {
  id: string;
  athlete: { id: string; givenName: string; familyName: string };
  /** The competition the suspension applies to; null means every competition of the club. */
  competition: { id: string; name: string } | null;
  startsOn: string;
  /** Set for a suspension that runs until a day; null for one counted in games. */
  endsOn: string | null;
  /** Set for a suspension counted in games; null for one that runs until a day. */
  remainingGames: number | null;
  decisionReference: string | null;
  status: SuspensionStatus;
  closingNote: string | null;
  version: number;
  /** Holds `competitions.manage`, so may correct, serve or revoke this suspension. */
  canManage: boolean;
}

/**
 * Competition matches (WP07, ADR 0016). A match ties one scheduled event of type match to a
 * competition season, the club's team playing it, a phase of the competition, an opponent label and
 * home or away. It starts scheduled; a registrar corrects it while it is scheduled, marks it
 * completed (recording the rule profile version active at that moment) or cancels it with a reason.
 * A completed or cancelled match never changes.
 */
export const matchStatuses = ['scheduled', 'completed', 'cancelled'] as const;
export type MatchStatus = (typeof matchStatuses)[number];

export const matchHomeAway = ['home', 'away'] as const;
export type MatchHomeAway = (typeof matchHomeAway)[number];

export const createMatchRequestSchema = z.strictObject({
  competitionSeasonId: z.uuid(),
  eventId: z.uuid(),
  teamId: z.uuid(),
  phase: z.enum(competitionPhases),
  opponentLabel: textSchema(100),
  homeAway: z.enum(matchHomeAway),
});
export type CreateMatchRequest = z.infer<typeof createMatchRequestSchema>;

/** Only a scheduled match can be corrected; the event, the team and the competition never change. */
export const updateMatchRequestSchema = z
  .strictObject({
    phase: z.enum(competitionPhases).optional(),
    opponentLabel: textSchema(100).optional(),
    homeAway: z.enum(matchHomeAway).optional(),
  })
  .refine(
    (request) =>
      request.phase !== undefined ||
      request.opponentLabel !== undefined ||
      request.homeAway !== undefined,
  );
export type UpdateMatchRequest = z.infer<typeof updateMatchRequestSchema>;

/** A cancellation always carries its reason. */
export const cancelMatchRequestSchema = z.strictObject({ reason: textSchema(500) });
export type CancelMatchRequest = z.infer<typeof cancelMatchRequestSchema>;

export interface MatchView {
  id: string;
  competition: { id: string; name: string };
  event: {
    id: string;
    title: string;
    date: string;
    startTime: string;
    timeZone: string;
    cancelled: boolean;
  };
  team: { id: string; name: string };
  phase: CompetitionPhase;
  opponentLabel: string;
  homeAway: MatchHomeAway;
  status: MatchStatus;
  cancelReason: string | null;
  /** The rule profile version active when the match was completed; null if none was. */
  ruleProfile: { id: string; versionNumber: number } | null;
  version: number;
  /** Holds `competitions.manage` in the competition's department. */
  canManage: boolean;
}

/** A match event of the competition's department that does not host a match yet. */
export interface MatchCandidateEventView {
  id: string;
  title: string;
  date: string;
  startTime: string;
  timeZone: string;
}

/**
 * Draft rosters of matches (WP07, ADR 0017). A registrar selects athletes for a scheduled match, one
 * request per athlete, each checked against the roster version the caller last saw (0 while no
 * roster exists). The role is the role the athlete plays in this match. A selected entry does not
 * prove an appearance and carries no eligibility yet. Once the match is completed or cancelled its
 * roster no longer changes.
 */
export const rosterRoles = ['goalkeeper', 'defender', 'forward'] as const;
export type RosterRole = (typeof rosterRoles)[number];

export const maximumShirtNumber = 99;

export const setRosterEntryRequestSchema = z.strictObject({
  role: z.enum(rosterRoles),
  shirtNumber: z.number().int().min(0).max(maximumShirtNumber).nullable().default(null),
});
export type SetRosterEntryRequest = z.infer<typeof setRosterEntryRequestSchema>;

export interface MatchRosterEntryView {
  athlete: { id: string; givenName: string; familyName: string };
  role: RosterRole;
  shirtNumber: number | null;
}

export const rosterStatuses = ['draft', 'finalized'] as const;
export type RosterStatus = (typeof rosterStatuses)[number];

/**
 * Finalizing a roster (WP07, ADR 0019). The registrar finalizes the roster version last seen; it is
 * evaluated again first and refused when anyone is not eligible or the roster breaks a rule. When
 * anything still needs review the registrar must acknowledge it in words. A finalized roster is
 * frozen until it is reopened with a reason.
 */
export const finalizeRosterRequestSchema = z.strictObject({
  reviewAcknowledgement: textSchema(500).optional(),
});
export type FinalizeRosterRequest = z.infer<typeof finalizeRosterRequestSchema>;

export const reopenRosterRequestSchema = z.strictObject({ reason: textSchema(500) });
export type ReopenRosterRequest = z.infer<typeof reopenRosterRequestSchema>;

export interface MatchRosterView {
  matchId: string;
  /** The version to send as `If-Match` with the next change; 0 while no roster exists yet. */
  version: number;
  status: RosterStatus;
  /** When the roster was finalized; null for a draft. */
  finalizedAt: string | null;
  /** The registrar acknowledged open review items when finalizing. */
  reviewAcknowledged: boolean;
  entries: MatchRosterEntryView[];
  /** Holds `competitions.manage` in the competition's department, the roster is a draft and the match is scheduled. */
  canChange: boolean;
  /** Same permission, a draft with at least one entry and a scheduled match. */
  canFinalize: boolean;
  /** Same permission, a finalized roster and a scheduled match. */
  canReopen: boolean;
}

/**
 * Roster eligibility (WP07, ADR 0018). A registrar evaluates the roster of a scheduled match against
 * the competition's active rule profile version; the result is stored and never rewritten. Each
 * decision is ELIGIBLE, INELIGIBLE or REVIEW_REQUIRED with every reason kept; without an active
 * version nobody is ELIGIBLE. A decision whose inputs or rules changed since is shown as stale.
 */
export const eligibilityOutcomes = ['ELIGIBLE', 'INELIGIBLE', 'REVIEW_REQUIRED'] as const;
export type EligibilityOutcomeView = (typeof eligibilityOutcomes)[number];

export interface EligibilityReasonView {
  /** Stable machine-readable code that the web app translates. */
  code: string;
  outcome: Exclude<EligibilityOutcomeView, 'ELIGIBLE'>;
  sourceClause: string | null;
  /** Small scalar facts for the message, such as counts, limits and categories. */
  detail: Record<string, string | number | boolean>;
  /** The approved exception reference that resolved this reason, if any. */
  resolvedByException: string | null;
}

export interface EligibilityDecisionView {
  athlete: { id: string; givenName: string; familyName: string };
  role: RosterRole;
  outcome: EligibilityOutcomeView;
  reasons: EligibilityReasonView[];
  /** An input of this athlete or the active rule version changed since the evaluation. */
  stale: boolean;
  /** False when the athlete has been removed from the roster since. */
  onRoster: boolean;
}

export interface EligibilityEvaluationView {
  id: string;
  evaluatedAt: string;
  /** The match day in the competition's time zone, which every date rule used. */
  evaluatedOn: string;
  rosterVersion: number;
  ruleProfile: { id: string; versionNumber: number } | null;
  /** The roster as a whole: role counts, the promotion quota and its entries' outcomes. */
  outcome: EligibilityOutcomeView;
  reasons: EligibilityReasonView[];
  decisions: EligibilityDecisionView[];
  /** Entries of athletes outside the caller's scope: counted for the roster, not listed. */
  hiddenEntryCount: number;
}

export interface MatchEligibilityView {
  matchId: string;
  /** The current roster version; send it as `If-Match` to evaluate. */
  rosterVersion: number;
  /** The newest stored evaluation, or null when the roster was never evaluated. */
  evaluation: EligibilityEvaluationView | null;
  /** The roster changed since the stored evaluation. */
  rosterChanged: boolean;
  /** The competition's active rule version is no longer the one the evaluation used. */
  rulesChanged: boolean;
  /** Checks of the active version that no automated rule covers; they stay with the registrar. */
  mandatoryManualChecks: ManualCheckView[];
  /** Holds `competitions.manage` in the competition's department, the roster is a draft and the match is scheduled. */
  canEvaluate: boolean;
  /** Holds `competitions.manage` in the competition's department and the match is not cancelled. */
  canRecordManualChecks: boolean;
}

/**
 * Recorded manual checks (WP07, ADR 0021). A registrar records a mandatory manual check of the active
 * rule profile version as done, with a written statement of what was checked and the day it was
 * checked. A record counts only for the roster version and the rule profile version it was made
 * against, so changing the roster or activating other rules makes the check open again; it is
 * withdrawn with a reason.
 */
export const recordManualCheckRequestSchema = z.strictObject({
  statement: textSchema(500),
  checkedOn: calendarDateSchema,
});
export type RecordManualCheckRequest = z.infer<typeof recordManualCheckRequestSchema>;

export const withdrawManualCheckRequestSchema = z.strictObject({ reason: textSchema(500) });
export type WithdrawManualCheckRequest = z.infer<typeof withdrawManualCheckRequestSchema>;

export interface ManualCheckView {
  key: string;
  sourceClause: string;
  /** The record that counts at the current roster version and rule version; null while the check is open. */
  record: {
    /** Send as `If-Match` when withdrawing the record. */
    version: number;
    statement: string;
    checkedOn: string;
    recordedAt: string;
  } | null;
}

/**
 * External history (WP07, ADR 0022). The platform only knows the matches on this club's rosters, so a
 * registrar records the verified matches an athlete played elsewhere (an external appearance) and
 * attests that, for a stretch of days, every such match is recorded. Only a granted attestation that
 * reaches back over the longest workload window of the rules and up to the day before the match lets
 * the workload checks stop reporting incomplete history. Both kinds of record are withdrawn with a
 * reason; what was recorded never changes and a withdrawn record is final.
 */
export const externalAppearanceStatuses = ['recorded', 'withdrawn'] as const;
export type ExternalAppearanceStatus = (typeof externalAppearanceStatuses)[number];

export const externalAttestationStatuses = ['granted', 'withdrawn'] as const;
export type ExternalAttestationStatus = (typeof externalAttestationStatuses)[number];

/** The longest stretch one attestation can cover, in days; the same limit the database applies. */
export const maximumAttestationDays = 3660;

export const recordExternalAppearanceRequestSchema = z.strictObject({
  athleteId: z.uuid(),
  playedOn: calendarDateSchema,
  /** The competition or club the match was played for, as the registrar writes it. */
  competitionName: textSchema(200),
  /** Where the participation was verified: a match sheet number, a federation page or a document. */
  sourceReference: textSchema(200),
});
export type RecordExternalAppearanceRequest = z.infer<typeof recordExternalAppearanceRequestSchema>;

function daysBetween(from: string, through: string): number {
  return (Date.parse(`${through}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000;
}

export const recordExternalHistoryAttestationRequestSchema = z
  .strictObject({
    athleteId: z.uuid(),
    coversFrom: calendarDateSchema,
    coversThrough: calendarDateSchema,
    statement: textSchema(500),
  })
  .refine((request) => request.coversThrough >= request.coversFrom, { path: ['coversThrough'] })
  .refine(
    (request) => daysBetween(request.coversFrom, request.coversThrough) <= maximumAttestationDays,
    { path: ['coversThrough'] },
  );
export type RecordExternalHistoryAttestationRequest = z.infer<
  typeof recordExternalHistoryAttestationRequestSchema
>;

export const withdrawExternalHistoryRequestSchema = z.strictObject({ reason: textSchema(500) });
export type WithdrawExternalHistoryRequest = z.infer<typeof withdrawExternalHistoryRequestSchema>;

export interface ExternalAppearanceView {
  id: string;
  playedOn: string;
  competitionName: string;
  sourceReference: string;
  status: ExternalAppearanceStatus;
  withdrawalReason: string | null;
  recordedAt: string;
  /** Send as `If-Match` when withdrawing the record. */
  version: number;
}

export interface ExternalHistoryAttestationView {
  id: string;
  coversFrom: string;
  coversThrough: string;
  statement: string;
  status: ExternalAttestationStatus;
  withdrawalReason: string | null;
  recordedAt: string;
  /** Send as `If-Match` when withdrawing the record. */
  version: number;
}

export interface ExternalHistoryView {
  athlete: { id: string; givenName: string; familyName: string };
  /** The newest day first, withdrawn records included as history. */
  appearances: ExternalAppearanceView[];
  attestations: ExternalHistoryAttestationView[];
  /** Holds `competitions.manage`, so may record and withdraw. */
  canManage: boolean;
}

/**
 * Internal roster export (WP07, ADR 0020). Every export is labelled an internal aid and is never a
 * federation submission. It is official-ready only when the competition's profile is active, the
 * roster is finalized and listed in full, passes every check and every mandatory manual check is
 * recorded as done; otherwise the rows are a draft and the blockers say why. The web app turns the
 * rows into a CSV file with translated column headers.
 */
export const rosterExportBlockers = [
  'PROFILE_NOT_ACTIVE',
  'ROSTER_NOT_FINALIZED',
  'ENTRIES_NOT_VISIBLE',
  'ROSTER_NOT_ELIGIBLE',
  'MANUAL_CHECK_UNRESOLVED',
] as const;
export type RosterExportBlockerCode = (typeof rosterExportBlockers)[number];

export interface RosterExportRowView {
  shirtNumber: number | null;
  givenName: string;
  familyName: string;
  role: RosterRole;
  /** The athlete's outcome as evaluated for this export, never an approval by itself. */
  outcome: EligibilityOutcomeView;
}

export interface RosterExportView {
  matchId: string;
  kind: 'internal_aid';
  officialReady: boolean;
  /** `key` names the mandatory manual check for `MANUAL_CHECK_UNRESOLVED`, else null. */
  blockers: { code: RosterExportBlockerCode; key: string | null }[];
  rosterStatus: RosterStatus;
  rosterVersion: number;
  /** The rule profile version the export was evaluated on; null without an active version. */
  ruleProfile: { id: string; versionNumber: number } | null;
  /** The match day in the competition's time zone. */
  matchDay: string;
  /** The label of the opponent and the team, for the file header. */
  competitionName: string;
  opponentLabel: string;
  /** Entries of athletes outside the caller's scope: counted, never listed. */
  hiddenEntryCount: number;
  rows: RosterExportRowView[];
}
